/**
 * Applications — list + Pipeline (Kanban) board.  GET /applications
 * Dragging a card on the board opens the move-with-reason dialog (PATCH /:id/stage).
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, List as ListIcon, KanbanSquare } from 'lucide-react';
import { PageHeader, Button, FloatingInput, FloatingDropdown, Pagination, TableSkeleton } from '../../components/ui';
import { cn } from '../../utils/cn';
import {
	ApplicationsTable,
	PipelineBoard,
	MoveStageDialog,
	EmptyState,
	ErrorState,
} from '../../features/recruitment';
import { useApplications, useMoveStage } from '../../features/recruitment/hooks/useApplications';
import { useJobOptions, usePeopleOptions } from '../../features/recruitment/hooks/useOptions';
import { STAGES, STAGE_META, SOURCE_OPTIONS } from '../../features/recruitment/config/constants';
import { getRecruitmentError } from '../../features/recruitment/utils/apiError';
import type { ApplicationFilters, ApplicationRow, Stage } from '../../services/recruitmentApi';

const PAGE_SIZE = 25;
const BOARD_SIZE = 200;

const STAGE_OPTIONS = STAGES.map(s => ({ value: s, label: STAGE_META[s].label }));

export const ApplicationsList: React.FC = () => {
	const navigate = useNavigate();
	const [view, setView] = useState<'list' | 'pipeline'>('list');

	// Filters
	const [jobId, setJobId] = useState('');
	const [recruiterId, setRecruiterId] = useState('');
	const [stage, setStage] = useState('');
	const [source, setSource] = useState('');
	const [query, setQuery] = useState('');
	const [applied, setApplied] = useState<ApplicationFilters>({});
	const [page, setPage] = useState(1);

	const { data: jobOptions } = useJobOptions();
	const { data: people } = usePeopleOptions();

	const filters: ApplicationFilters = {
		...applied,
		...(view === 'list' ? { page, page_size: PAGE_SIZE } : { page: 1, page_size: BOARD_SIZE }),
	};
	const { data, isLoading, isError, refetch, isFetching } = useApplications(filters);
	const moveStage = useMoveStage();

	const rows = (data?.rows ?? []) as ApplicationRow[];
	const total = data?.total ?? 0;
	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
	const activeFilterCount = [jobId, recruiterId, stage, source, query].filter(Boolean).length;

	// Move dialog state
	const [move, setMove] = useState<{ app: ApplicationRow; to: Stage } | null>(null);
	const [moveError, setMoveError] = useState<string | null>(null);

	const runSearch = () => {
		setPage(1);
		setApplied({
			job_opening_id: jobId ? Number(jobId) : undefined,
			recruiter_id: recruiterId ? Number(recruiterId) : undefined,
			stage: (stage || undefined) as Stage | undefined,
			source: source || undefined,
			q: query.trim() || undefined,
		});
	};

	const clearFilters = () => {
		setJobId('');
		setRecruiterId('');
		setStage('');
		setSource('');
		setQuery('');
		setPage(1);
		setApplied({});
	};

	const confirmMove = async (to: Stage, reason: string) => {
		if (!move) return;
		setMoveError(null);
		try {
			await moveStage.mutateAsync({ id: move.app.id, body: { to, reason } });
			setMove(null);
			refetch();
		} catch (e) {
			setMoveError(getRecruitmentError(e).message);
		}
	};

	const viewToggle = useMemo(
		() => (
			<div className='inline-flex rounded-lg border border-gray-200 bg-white p-0.5'>
				{([
					{ id: 'list', label: 'List', icon: <ListIcon className='h-4 w-4' /> },
					{ id: 'pipeline', label: 'Pipeline', icon: <KanbanSquare className='h-4 w-4' /> },
				] as const).map(opt => (
					<button
						key={opt.id}
						type='button'
						onClick={() => setView(opt.id)}
						className={cn(
							'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition',
							view === opt.id ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'
						)}
					>
						{opt.icon}
						{opt.label}
					</button>
				))}
			</div>
		),
		[view]
	);

	return (
		<div className='min-h-screen bg-gray-50 p-6'>
			<div className='mx-auto max-w-7xl'>
				<div className='mb-6 flex items-start justify-between'>
					<PageHeader title='Applications' totalItems={total} itemType='applications' icon='📋' />
					<div className='flex items-center gap-3'>
						{viewToggle}
						<Button variant='primary' leftIcon={<Plus className='h-4 w-4' />} onClick={() => navigate('/recruitment/candidates/add')}>
							Add Candidate
						</Button>
					</div>
				</div>

				{/* Filters */}
				<div className='mb-6 rounded-lg bg-white p-4 shadow-sm'>
					<div className='grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-5'>
						<FloatingInput label='Candidate name' value={query} onChange={setQuery} />
						<FloatingDropdown label='Job Opening' options={jobOptions ?? []} value={jobId} onChange={setJobId} />
						<FloatingDropdown label='Recruiter' options={people ?? []} value={recruiterId} onChange={setRecruiterId} />
						<FloatingDropdown label='Stage' options={STAGE_OPTIONS} value={stage} onChange={setStage} searchable={false} />
						<FloatingDropdown label='Source' options={SOURCE_OPTIONS} value={source} onChange={setSource} searchable={false} />
					</div>
					<div className='mt-4 flex items-center justify-between'>
						<span className='text-sm text-gray-500'>
							{activeFilterCount > 0 ? `${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} applied` : 'No filters applied'}
						</span>
						<div className='flex gap-2'>
							{activeFilterCount > 0 && (
								<Button variant='ghost' size='sm' leftIcon={<X className='h-3.5 w-3.5' />} onClick={clearFilters}>
									Clear
								</Button>
							)}
							<Button variant='primary' size='sm' onClick={runSearch} loading={isFetching}>
								Apply
							</Button>
						</div>
					</div>
				</div>

				{/* Content */}
				{isLoading ? (
					<TableSkeleton rows={8} columns={7} />
				) : isError ? (
					<ErrorState onRetry={() => refetch()} />
				) : rows.length === 0 ? (
					<div className='rounded-lg bg-white shadow-sm'>
						<EmptyState
							title='No applications found'
							message={activeFilterCount > 0 ? 'Try adjusting or clearing your filters.' : 'Add a candidate to create the first application.'}
							icon={<KanbanSquare className='h-7 w-7' />}
							action={
								<Button variant='primary' leftIcon={<Plus className='h-4 w-4' />} onClick={() => navigate('/recruitment/candidates/add')}>
									Add Candidate
								</Button>
							}
						/>
					</div>
				) : view === 'list' ? (
					<>
						<div className='overflow-hidden rounded-lg bg-white shadow-sm'>
							<ApplicationsTable rows={rows} onOpen={id => navigate(`/recruitment/applications/${id}`)} />
						</div>
						<div className='mt-4'>
							<Pagination
								currentPage={page}
								totalPages={totalPages}
								totalItems={total}
								itemsPerPage={PAGE_SIZE}
								onPageChange={setPage}
							/>
						</div>
					</>
				) : (
					<>
						<PipelineBoard
							rows={rows}
							onOpen={id => navigate(`/recruitment/applications/${id}`)}
							onRequestMove={(app, to) => {
								setMoveError(null);
								setMove({ app, to });
							}}
						/>
						{total > rows.length && (
							<p className='mt-3 text-center text-xs text-gray-400'>
								Showing the first {rows.length} of {total} applications on the board. Narrow with filters to see specific cards.
							</p>
						)}
					</>
				)}
			</div>

			{move && (
				<MoveStageDialog
					open
					onClose={() => {
						setMove(null);
						setMoveError(null);
					}}
					currentStage={move.app.stage}
					initialTo={move.to}
					candidateName={move.app.candidate_name}
					loading={moveStage.isPending}
					error={moveError}
					onConfirm={confirmMove}
				/>
			)}
		</div>
	);
};
